import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../utils/api';

const REMEMBERED_EMAIL_KEY = 'adminRememberedEmail';

const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Priority: router state (soft redirect from guard) → sessionStorage (hard
  // nav from 401 interceptor) → default. Resolved once on mount and pinned in
  // state so handleLogin (which fires on a later render) still sees it after
  // we consume the sessionStorage entry.
  const [redirectTo] = useState(() => {
    const fromState = (location.state as { from?: string } | null)?.from;
    if (fromState) return fromState;
    try {
      const stashed = sessionStorage.getItem('adminPostLoginReturn');
      if (stashed) {
        sessionStorage.removeItem('adminPostLoginReturn');
        return stashed;
      }
    } catch {}
    return '/admin';
  });

  useEffect(() => {
    // Already signed in? Skip the form and jump straight to the intended page.
    if (localStorage.getItem('adminToken')) {
      navigate(redirectTo, { replace: true });
      return;
    }
    const remembered = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (remembered) {
      setEmail(remembered);
      setRememberMe(true);
    } else {
      setRememberMe(false);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      localStorage.setItem('adminToken', response.data.token);

      if (rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }

      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#07090e] text-slate-300 font-sans flex items-center justify-center px-4 py-12">
      {/* Ambient background glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full bg-emerald-500/15 blur-[120px]" />
        <div className="absolute top-1/3 -right-32 w-[520px] h-[520px] rounded-full bg-blue-500/15 blur-[140px]" />
        <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full bg-fuchsia-500/10 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Link to="/" className="group inline-flex items-center gap-2 mb-6">
            <img
              src="/logo-2.png"
              alt="LANForge"
              className="h-9 w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity drop-shadow-[0_0_18px_rgba(16,185,129,0.35)]"
            />
          </Link>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-400 bg-emerald-500/10 border border-emerald-500/25">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Restricted Area
          </span>
          <h2 className="mt-4 text-3xl font-extrabold text-white tracking-tight">
            Admin Sign-In
          </h2>
          <p className="mt-2 text-sm text-slate-400 text-center">
            Sign in with your LANForge admin credentials to continue.
          </p>
        </div>

        <div className="relative">
          {/* Gradient border glow */}
          <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-emerald-500/40 via-blue-500/20 to-transparent opacity-60 blur-[2px]" />
          <div className="relative bg-[#0a0c13]/90 backdrop-blur-xl border border-[#1f2233] rounded-2xl shadow-2xl shadow-black/60 p-8">
            <form className="space-y-5" onSubmit={handleLogin} autoComplete="on">
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2 bg-red-500/10 border border-red-500/40 text-red-300 p-3 rounded-lg text-sm"
                >
                  <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label htmlFor="admin-email" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </span>
                  <input
                    id="admin-email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@lanforge.co"
                    className="w-full pl-10 pr-3 py-2.5 bg-[#11141d] border border-[#1f2233] rounded-lg text-white placeholder-slate-600 text-sm focus:outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="admin-password" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c1.657 0 3-1.343 3-3V6a3 3 0 00-6 0v2c0 1.657 1.343 3 3 3zm6 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2v-6a2 2 0 012-2h8a2 2 0 012 2z" />
                    </svg>
                  </span>
                  <input
                    id="admin-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-11 py-2.5 bg-[#11141d] border border-[#1f2233] rounded-lg text-white placeholder-slate-600 text-sm tracking-wider focus:outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-emerald-400 transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-7 0-10-7-10-7a18.45 18.45 0 014.05-5.5M9.88 4.24A9.95 9.95 0 0112 4c7 0 10 7 10 7a18.5 18.5 0 01-2.16 3.19M6.4 6.4l11.2 11.2M9.88 9.88a3 3 0 104.24 4.24" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                        <circle cx="12" cy="12" r="3" strokeWidth={2} />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <label htmlFor="remember-me" className="inline-flex items-center gap-2 cursor-pointer select-none group pt-1">
                <span className="relative">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="peer sr-only"
                  />
                  <span className="block w-4 h-4 rounded border border-[#2a2f42] bg-[#11141d] peer-checked:bg-gradient-to-br peer-checked:from-emerald-400 peer-checked:to-blue-500 peer-checked:border-transparent transition-all" />
                  <svg className="absolute top-0 left-0 w-4 h-4 text-[#07090e] opacity-0 peer-checked:opacity-100 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-xs text-slate-400 group-hover:text-slate-200 transition-colors">
                  Remember my email on this device
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full relative flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-400 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0a0c13] focus:ring-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/20 transition-all"
              >
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          <Link to="/" className="hover:text-emerald-400 transition-colors">
            ← Back to LANForge
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
